import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MOTIVOS_DA_MUDANCA_DE_PRAZO } from "@bookdental/schemas";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";

const paraYmd = (d: Date | string | null) => (d ? format(new Date(d), "yyyy-MM-dd") : "");
// "YYYY-MM-DD" como meia-noite local, igual ao envio do pedido (new Date("YYYY-MM-DD") seria UTC).
const deYmd = (ymd: string) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};

/** O laboratório muda o prazo do pedido. Motivo opcional, com respostas prontas; a clínica é avisada. */
export function AlterarPrazoDialog({
  pedido,
  onFechar,
}: {
  pedido: { id: string; deadline: Date | string | null; patientName: string } | null;
  onFechar: () => void;
}) {
  const utils = trpc.useUtils();
  const [data, setData] = useState("");
  const [motivo, setMotivo] = useState("");
  useEffect(() => {
    if (pedido) {
      setData(paraYmd(pedido.deadline));
      setMotivo("");
    }
  }, [pedido]);

  const alterar = trpc.prosthesis.prazo.alterar.useMutation({
    onSuccess: ({ mudou }) => {
      toast.success(mudou ? "Prazo alterado. A clínica foi avisada." : "O prazo já era esse.");
      // O prazo aparece na lista, no quadro, na planilha, na agenda de entregas e nos contadores de
      // atraso — todos debaixo de `prosthesis`.
      utils.prosthesis.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message || "Não foi possível alterar o prazo."),
  });

  const hoje = format(new Date(), "yyyy-MM-dd");
  const mudou = !!data && data !== paraYmd(pedido?.deadline ?? null);

  return (
    <Dialog open={!!pedido} onOpenChange={(v) => !v && !alterar.isPending && onFechar()}>
      <DialogContent className="max-w-md border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card" onClick={(e) => e.stopPropagation()}>
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-brand-600" /> Alterar prazo
          </DialogTitle>
          <DialogDescription>
            Pedido de {pedido?.patientName}. A clínica é avisada e vê no pedido o prazo que tinha pedido.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-zinc-600 dark:text-muted-foreground">Novo prazo</span>
            <input
              type="date"
              value={data}
              min={hoje}
              onChange={(e) => setData(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
            />
          </label>

          <div className="space-y-2">
            <p className="text-xs font-medium text-zinc-600 dark:text-muted-foreground">Motivo (opcional)</p>
            <div className="flex flex-wrap gap-1.5">
              {MOTIVOS_DA_MUDANCA_DE_PRAZO.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMotivo(m)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                    motivo === m
                      ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                      : "border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted",
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
            <textarea
              aria-label="Motivo da mudança de prazo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="Escolha uma resposta acima ou escreva..."
              className="w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onFechar}
            disabled={alterar.isPending}
            className="h-9 rounded-md border border-zinc-200 px-4 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!mudou || data < hoje || alterar.isPending}
            onClick={() => alterar.mutate({ orderId: pedido!.id, deadline: deYmd(data), motivo: motivo.trim() || undefined })}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {alterar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar prazo
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
